<template>
  <section id="rights-view" class="enterprise-section enterprise-comparison" aria-labelledby="enterprise-comparison-title">
    <div class="enterprise-shell">
      <div class="enterprise-section-heading">
        <h2 id="enterprise-comparison-title">{{ t('enterprise_home_nav_comparison') }}</h2>
        <p>{{ comparisonDescription }}</p>
      </div>

      <div class="enterprise-segmented" role="tablist" :aria-label="t('enterprise_home_comparison_region_aria')">
        <button type="button" :class="{ active: mode === 'local' }" @click="$emit('update:mode', 'local')">
          <EnterpriseIcon :src="mapPinIcon" />{{ localModeLabel }}
        </button>
        <div class="enterprise-segmented__option" :class="{ active: mode === 'global' }">
          <button type="button" class="enterprise-segmented__mode-button" @click="$emit('update:mode', 'global')">
            <EnterpriseIcon :src="globeIcon" />{{ globalModeLabel }}
          </button>
          <span
            ref="comparisonInfoRef"
            class="enterprise-segmented__info-wrap"
            @mouseenter="scheduleComparisonInfo"
            @mouseleave="hideComparisonInfo"
          >
            <button
              type="button"
              class="enterprise-segmented__info-trigger"
              :class="{ 'is-open': comparisonInfoVisible }"
              :aria-label="t('enterprise_home_comparison_info_aria')"
              :aria-expanded="comparisonInfoVisible"
              aria-controls="enterprise-comparison-info-tooltip"
              @click.stop.prevent="toggleComparisonInfo"
              @focus="showComparisonInfo"
              @blur="hideComparisonInfo"
              @keydown.enter.stop.prevent="toggleComparisonInfo"
              @keydown.space.stop.prevent="toggleComparisonInfo"
              @keydown.esc.stop.prevent="closeComparisonInfo"
            >
              <EnterpriseIcon :src="circleAlertIcon" />
            </button>
            <span
              v-show="comparisonInfoVisible"
              id="enterprise-comparison-info-tooltip"
              class="enterprise-comparison-tooltip"
              role="tooltip"
              @click.stop
            >
              {{ comparisonInfoText }}
            </span>
          </span>
        </div>
      </div>

      <div class="enterprise-comparison__scroll">
        <div class="enterprise-comparison-table" :style="{ '--enterprise-comparison-plan-count': planNames.length }">
          <div class="enterprise-comparison-table__header enterprise-comparison-table__grid">
            <div>{{ t('enterprise_home_comparison_header') }}</div>
            <div v-for="(plan, index) in planNames" :key="plan">
              <strong>{{ plan }}</strong>
              <small>{{ prices[index] }}</small>
            </div>
          </div>

          <template v-for="section in sections" :key="section.title">
            <div class="enterprise-comparison-table__section">{{ section.title }}</div>
            <div v-for="row in section.rows" :key="row.label" class="enterprise-comparison-table__row enterprise-comparison-table__grid">
              <div>{{ row.label }}</div>
              <div v-for="(value, index) in row.values" :key="index">
                <EnterpriseIcon v-if="value === true" class="enterprise-table-check" :src="circleCheckIcon" />
                <span v-else-if="value === false" class="enterprise-table-empty">—</span>
                <span v-else>{{ value }}</span>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import EnterpriseIcon from '~/components/Index/enterprise/EnterpriseIcon.vue'
import circleAlertIcon from '~/assets/images/enterprise/icons/circle-alert.svg'
import circleCheckIcon from '~/assets/images/enterprise/icons/circle-check.svg'
import globeIcon from '~/assets/images/enterprise/icons/globe.svg'
import mapPinIcon from '~/assets/images/enterprise/icons/map-pin.svg'
import {
  getEnterpriseComparison,
  getEnterpriseCurrencySymbol,
  getEnterprisePlanPrices,
  type EnterpriseCatalogData,
  type EnterprisePricingMode,
} from '~/config/enterpriseLanding'
import { useSiteConfig } from '~/config/useSiteConfig'
import useProStore from '~/store/modules/pro'
import useUserStore from '~/store/modules/user'

const props = defineProps<{ mode: EnterprisePricingMode }>()
defineEmits<{ 'update:mode': [mode: EnterprisePricingMode] }>()
const { siteEdition, isVipSite } = useSiteConfig()
const { locale, t } = useI18n()
const proStore = useProStore()
const userStore = useUserStore()
const { proConfig, globalProConfig, isEnterpriseCatalogReady } = storeToRefs(proStore)
const { roleRightsMap } = storeToRefs(userStore)
const currentCurrency = computed(() => userStore.getCurrentCoin(locale.value))
const currentCurrencySymbol = computed(() => getEnterpriseCurrencySymbol(currentCurrency.value))
const catalog = computed<EnterpriseCatalogData>(() => ({
  currency: currentCurrency.value,
  localPrices: isEnterpriseCatalogReady.value ? proConfig.value : [],
  globalPrices: isEnterpriseCatalogReady.value ? globalProConfig.value : [],
  rights: roleRightsMap.value,
}))
const planNames = computed(() => [
  t('enterprise_home_plan_base'),
  t('enterprise_home_plan_professional'),
  t('enterprise_home_plan_advanced'),
  t('enterprise_home_plan_custom'),
])
const localModeLabel = computed(() => t(isVipSite.value ? 'enterprise_home_mode_cn' : 'enterprise_home_mode_ai', { currency: currentCurrencySymbol.value }))
const globalModeLabel = computed(() => t(isVipSite.value ? 'enterprise_home_mode_global_collaboration' : 'enterprise_home_mode_global_with_china'))
const comparisonDescription = computed(() => props.mode === 'global'
  ? t('enterprise_home_comparison_description_global')
  : t('enterprise_home_comparison_description_local'))
const comparisonInfoText = computed(() => isVipSite.value
  ? t('enterprise_home_pricing_info_cn')
  : t('enterprise_home_pricing_info_ai'))
const prices = computed(() => getEnterprisePlanPrices(siteEdition.value, props.mode, catalog.value, t, locale.value))
const sections = computed(() => getEnterpriseComparison(siteEdition.value, props.mode, catalog.value, t, locale.value))
const comparisonInfoRef = ref<HTMLElement | null>(null)
const comparisonInfoVisible = ref(false)
const comparisonInfoPinned = ref(false)
let comparisonInfoTimer: ReturnType<typeof setTimeout> | undefined

function clearComparisonInfoTimer() {
  if (comparisonInfoTimer) {
    clearTimeout(comparisonInfoTimer)
    comparisonInfoTimer = undefined
  }
}

function scheduleComparisonInfo() {
  clearComparisonInfoTimer()
  comparisonInfoTimer = setTimeout(() => {
    comparisonInfoVisible.value = true
    comparisonInfoTimer = undefined
  }, 300)
}

function showComparisonInfo() {
  clearComparisonInfoTimer()
  comparisonInfoVisible.value = true
}

function hideComparisonInfo() {
  clearComparisonInfoTimer()
  if (!comparisonInfoPinned.value) comparisonInfoVisible.value = false
}

function toggleComparisonInfo() {
  clearComparisonInfoTimer()
  comparisonInfoPinned.value = !comparisonInfoPinned.value
  comparisonInfoVisible.value = comparisonInfoPinned.value
}

function closeComparisonInfo() {
  clearComparisonInfoTimer()
  comparisonInfoPinned.value = false
  comparisonInfoVisible.value = false
}

function handleComparisonInfoOutsideClick(event: MouseEvent) {
  if (comparisonInfoPinned.value && !comparisonInfoRef.value?.contains(event.target as Node)) closeComparisonInfo()
}

onMounted(() => document.addEventListener('click', handleComparisonInfoOutsideClick))
onBeforeUnmount(() => {
  clearComparisonInfoTimer()
  document.removeEventListener('click', handleComparisonInfoOutsideClick)
})
</script>
